import React, { useEffect, useRef } from 'react';

/* ── Scroll reveal ── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('rv'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`rv-base ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

/* ── Section wrapper ── */
const S = ({ children, className = '' }) => (
  <section className={`px-6 sm:px-10 md:px-16 lg:px-24 ${className}`}>
    <div className="max-w-[880px] mx-auto">{children}</div>
  </section>
);

/* ── Divider ── */
const Divider = () => (
  <div className="max-w-[880px] mx-auto px-6">
    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
  </div>
);

/* ── Section label ── */
const Label = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.25em] text-[#4FD1C5]/60 mb-8">{children}</p>
);

/* ── Bullet item ── */
const Bullet = ({ children }) => (
  <div className="flex items-start gap-3">
    <div className="w-1 h-1 rounded-full bg-[#4FD1C5]/50 mt-2.5 flex-shrink-0" />
    <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">{children}</p>
  </div>
);

/* ── Revenue card ── */
const RevCard = ({ title, desc }) => (
  <div className="p-5 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
    <p className="text-sm font-semibold text-white mb-1.5">{title}</p>
    <p className="text-xs text-[#A1A1AA] leading-relaxed">{desc}</p>
  </div>
);

/* ── Browser mockup frame ── */
const BrowserFrame = ({ src, caption }) => (
  <div className="flex flex-col">
    <div className="rounded-xl border border-white/[0.08] bg-[#111113] overflow-hidden shadow-2xl shadow-black/40">
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#161618] border-b border-white/[0.04]">
        <div className="w-2 h-2 rounded-full bg-[#FF5F57]/70" />
        <div className="w-2 h-2 rounded-full bg-[#FEBC2E]/70" />
        <div className="w-2 h-2 rounded-full bg-[#28C840]/70" />
        <div className="ml-3 flex-1 h-4 rounded bg-white/[0.04]" />
      </div>
      <div className="aspect-[4/3] overflow-hidden">
        <img src={src} alt={caption} className="w-full h-full object-cover object-top" loading="lazy" />
      </div>
    </div>
    <p className="text-xs text-[#71717A] mt-3 leading-relaxed">{caption}</p>
  </div>
);

/* ═══════════════════════════════════════════════════════ */

const ExplainPage = () => {
  useEffect(() => {
    document.title = 'Kindra Health — Investor Briefing';
    document.body.style.backgroundColor = '#0B0B0D';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  return (
    <div
      className="bg-[#0B0B0D] text-white min-h-screen overflow-x-hidden selection:bg-[#4FD1C5]/20"
      style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}
      data-testid="explain-page"
    >
      <style>{`
        .rv-base{opacity:0;transform:translateY(20px);transition:opacity .7s ease,transform .7s ease}
        .rv-base.rv{opacity:1;transform:translateY(0)}
        html,body{overflow-x:hidden}
      `}</style>

      {/* ── Logo ── */}
      <div className="fixed top-6 left-6 sm:left-10 z-50">
        <img src="/kindra-brand-logo.png" alt="Kindra Health" className="h-10 w-10 object-contain opacity-60" />
      </div>

      {/* ═══════════════════════════════════════════════════
          SECTION 1 — HERO
          ═══════════════════════════════════════════════════ */}
      <S className="pt-32 sm:pt-44 pb-28 sm:pb-36 text-center">
        <R>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-8">
            From health confusion<br />to clear decisions.
          </h1>
        </R>
        <R delay={200}>
          <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed max-w-[600px] mx-auto mb-5">
            Most people are managing symptoms, medications, and conditions — but still don't understand what's actually happening.
          </p>
        </R>
        <R delay={350}>
          <p className="text-sm sm:text-base text-[#71717A] max-w-[560px] mx-auto leading-relaxed">
            Kindra Health turns fragmented personal health data into clear, grounded next steps — in real time.
          </p>
        </R>
        <R delay={500}>
          <div className="mt-20 flex flex-col items-center">
            <div className="w-px h-10 bg-gradient-to-b from-transparent to-[#4FD1C5]/30" />
            <div className="w-2 h-2 rounded-full bg-[#4FD1C5]/40 animate-pulse" />
          </div>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 2 — THE REAL PROBLEM
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>The problem</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-6">
            Health is not failing because of a lack of data.
          </h2>
        </R>
        <R delay={200}>
          <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed mb-10 max-w-[640px]">
            It's failing because information is fragmented, signals are disconnected, and nothing connects in a way that makes sense.
          </p>
        </R>
        <R delay={300}>
          <div className="space-y-4 mb-10">
            <Bullet>Symptoms exist in isolation</Bullet>
            <Bullet>Medications are managed separately</Bullet>
            <Bullet>Conditions are misunderstood</Bullet>
            <Bullet>Advice is generic</Bullet>
          </div>
        </R>
        <R delay={400}>
          <p className="text-sm sm:text-base text-[#71717A] leading-relaxed">
            A person can have multiple symptoms, medications, and conditions — and still not know what to do next.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION — HUMAN SCENARIO
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>What actually happens</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-10">
            This is what people actually experience
          </h2>
        </R>

        <R delay={200}>
          <div className="space-y-4 mb-10 max-w-[640px]">
            <p className="text-base text-[#A1A1AA] leading-relaxed">A person wakes up feeling off.</p>
            <p className="text-base text-[#A1A1AA] leading-relaxed">
              They're more tired than usual.<br />
              Their sleep hasn't been right.<br />
              They're on medication.<br />
              Something doesn't feel normal.
            </p>
          </div>
        </R>

        <R delay={300}>
          <p className="text-base text-white font-medium mb-5">They start thinking:</p>
          <div className="space-y-3 mb-10">
            <Bullet>Is this serious?</Bullet>
            <Bullet>Is this related to my medication?</Bullet>
            <Bullet>Do I need to change something?</Bullet>
            <Bullet>Should I see someone?</Bullet>
          </div>
        </R>

        <R delay={400}>
          <p className="text-base text-white font-medium mb-5">So they:</p>
          <div className="space-y-3 mb-10">
            <Bullet>Google symptoms</Bullet>
            <Bullet>Read conflicting information</Bullet>
            <Bullet>Try to piece things together</Bullet>
            <Bullet>Get more confused</Bullet>
          </div>
        </R>

        <R delay={500}>
          <p className="text-sm sm:text-base text-[#71717A] leading-relaxed">
            They don't need more information.<br />
            They need clarity on what's actually happening — and what to do next.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION — CONSEQUENCE
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R>
          <p className="text-xl sm:text-2xl font-light text-white leading-snug mb-6">
            This leads to delayed decisions, poor self-management, and avoidable escalation.
          </p>
        </R>
        <R delay={200}>
          <p className="text-base text-[#71717A] leading-relaxed">
            Not because people don't care — but because they can't see clearly.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 4 — THE SYSTEM
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>The system</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-10">
            Kindra Health acts as a decision layer.
          </h2>
        </R>

        {/* Flow diagram */}
        <R delay={200}>
          <div className="flex items-center justify-center gap-4 sm:gap-8 mb-14">
            {['INPUT', 'INTELLIGENCE', 'ACTION'].map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="w-8 sm:w-16 h-px bg-gradient-to-r from-[#4FD1C5]/20 to-[#4FD1C5]/40" />}
                <span className={`text-xs sm:text-sm font-bold tracking-[0.15em] ${i === 1 ? 'text-[#4FD1C5]' : i === 2 ? 'text-white' : 'text-[#A1A1AA]'}`}>
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </R>

        <R delay={300}>
          <div className="space-y-5 mb-10">
            {[
              'Collects real-world inputs',
              'Connects them into context',
              'Identifies meaningful patterns',
              'Produces clear direction',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-4">
                <span className="text-xs font-bold text-[#4FD1C5]/40 mt-0.5 w-4 flex-shrink-0">{i + 1}</span>
                <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </R>

        <R delay={400}>
          <p className="text-sm text-[#4FD1C5] font-medium">This is not tracking. This is decision support.</p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 5 — PROOF (CRITICAL)
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Proof</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            What this looks like in reality
          </h2>
        </R>
        <R delay={150}>
          <p className="text-base text-[#A1A1AA] leading-relaxed mb-12">
            Instead of guessing, they get this:
          </p>
        </R>

        <R delay={200}>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.015] overflow-hidden">
            {/* User message */}
            <div className="px-6 pt-6 pb-4 border-b border-white/[0.04]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-3">User</p>
              <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed italic">
                "I've been feeling off for a few days. I'm more tired than usual, my sleep's been bad, and I'm on medication. I don't know if it's related or if I should be worried."
              </p>
            </div>

            {/* Kindra response */}
            <div className="px-6 py-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#4FD1C5]/60 mb-5">Kindra</p>

              <div className="space-y-5">
                {/* Pattern */}
                <div className="flex items-start gap-3">
                  <span className="text-[#4FD1C5] mt-0.5 flex-shrink-0">&rarr;</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Pattern identified</p>
                    <p className="text-sm text-white leading-relaxed">Fatigue + disrupted sleep</p>
                  </div>
                </div>

                {/* Context */}
                <div className="flex items-start gap-3">
                  <span className="text-[#4FD1C5] mt-0.5 flex-shrink-0">&rarr;</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#71717A] mb-1">Context</p>
                    <p className="text-sm text-white leading-relaxed">Symptoms are compounding</p>
                  </div>
                </div>

                {/* Possible contributors */}
                <div className="flex items-start gap-3">
                  <span className="text-[#4FD1C5] mt-0.5 flex-shrink-0">&rarr;</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#71717A] mb-2">Possible contributors</p>
                    <div className="space-y-1.5">
                      <p className="text-sm text-[#A1A1AA]">Medication timing</p>
                      <p className="text-sm text-[#A1A1AA]">Sleep consistency</p>
                    </div>
                  </div>
                </div>

                {/* Next steps */}
                <div className="flex items-start gap-3">
                  <span className="text-[#4FD1C5] mt-0.5 flex-shrink-0">&rarr;</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#71717A] mb-2">Next steps</p>
                    <div className="space-y-1.5">
                      <p className="text-sm text-white">Adjust medication timing</p>
                      <p className="text-sm text-white">Track sleep patterns</p>
                      <p className="text-sm text-white">Monitor fatigue changes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </R>

        <R delay={350}>
          <p className="text-sm text-[#71717A] mt-8">
            Kindra does not diagnose. It creates clarity.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 6 — PRODUCT
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Product</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            This is already being used
          </h2>
        </R>
        <R delay={200}>
          <div className="space-y-4 mb-10 max-w-[680px]">
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              Kindra Health is live and being used to track real symptoms, medications, and health context.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              Users interact naturally, build a personal health profile over time, and receive responses that improve as more context is gathered.
            </p>
          </div>
        </R>
        <R delay={300}>
          <div className="space-y-4 mb-10">
            <Bullet>Input symptoms, medications, conditions</Bullet>
            <Bullet>Natural chat interface</Bullet>
            <Bullet>Context-aware responses</Bullet>
            <Bullet>Persistent, improving user profile</Bullet>
          </div>
        </R>
        <R delay={400}>
          <p className="text-sm text-[#4FD1C5] font-medium">
            The system becomes more valuable the more it is used.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 7 — SCREENSHOTS
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            <BrowserFrame src="/screenshots/home.png" caption="Simple onboarding" />
            <BrowserFrame src="/screenshots/chat.png" caption="Natural interaction" />
            <BrowserFrame src="/screenshots/insights.png" caption="Context-aware intelligence" />
            <BrowserFrame src="/screenshots/health.png" caption="Structured health input" />
          </div>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 8 — REVENUE
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Revenue</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
            How Kindra Health makes money
          </h2>
        </R>
        <R delay={200}>
          <div className="mb-10">
            <p className="text-lg sm:text-xl font-semibold text-white mb-1">$14.99/month subscription</p>
            <p className="text-sm text-[#71717A]">Per user, recurring</p>
          </div>
        </R>
        <R delay={300}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <RevCard title="Medical Centres" desc="Patient engagement layer" />
            <RevCard title="Insurance" desc="Risk reduction" />
            <RevCard title="Government / NDIS" desc="Guided care support" />
            <RevCard title="Employers" desc="Workforce health" />
          </div>
        </R>
        <R delay={400}>
          <p className="text-sm text-[#71717A]">One system. Multiple revenue streams.</p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 9 — WHY THIS WORKS
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Why this works</Label></R>
        <R delay={100}>
          <div className="space-y-6 max-w-[600px] mb-10">
            <p className="text-lg sm:text-xl text-[#A1A1AA] leading-relaxed">
              Data is increasing rapidly.<br />
              <span className="text-[#71717A]">Understanding is not.</span>
            </p>
            <p className="text-lg sm:text-xl text-[#A1A1AA] leading-relaxed">
              AI without context creates noise.<br />
              <span className="text-white font-medium">Context creates decisions.</span>
            </p>
          </div>
        </R>
        <R delay={250}>
          <div className="h-px bg-gradient-to-r from-transparent via-[#4FD1C5]/25 to-transparent mb-6" />
          <p className="text-base font-semibold text-[#4FD1C5]">
            Kindra converts fragmented data into clear action.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION — WHY NOW
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Why now</Label></R>
        <R delay={100}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-10">
            Why now
          </h2>
        </R>
        <R delay={200}>
          <div className="space-y-5 max-w-[680px] mb-10">
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              More health data is being generated than ever before.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              Wearables, apps, medications, and digital records have increased visibility — but not understanding.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              At the same time, expectations have shifted.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              People are no longer satisfied with generic advice or delayed answers.<br />
              They expect real-time clarity.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              AI has made it possible to process complex, fragmented inputs.
            </p>
            <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
              What hasn't existed — until now — is a system that applies that capability to real-world personal health decisions.
            </p>
          </div>
        </R>
        <R delay={350}>
          <p className="text-sm text-[#4FD1C5] font-medium">
            This shift makes a decision layer not just possible — but necessary.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 10 — DEFENSIBILITY
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Defensibility</Label></R>
        <R delay={100}>
          <div className="space-y-4 mb-10">
            {[
              'Context builds per user',
              'Data becomes unique',
              'Switching cost increases',
              'Intelligence improves over time',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-4 py-3 border-b border-white/[0.04]">
                <div className="w-6 h-6 rounded-full border border-[#4FD1C5]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]/60" />
                </div>
                <p className="text-sm sm:text-base text-[#A1A1AA]">{text}</p>
              </div>
            ))}
          </div>
        </R>
        <R delay={250}>
          <p className="text-sm text-[#71717A] mb-3">The system becomes more valuable the longer it is used.</p>
          <p className="text-sm text-[#71717A]">As context deepens, the system becomes increasingly difficult to replace.</p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 11 — SCALE
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R><Label>Scale</Label></R>
        <R delay={100}>
          <p className="text-xl sm:text-2xl font-light text-white leading-snug mb-8">
            The problem Kindra solves exists everywhere.
          </p>
        </R>
        <R delay={200}>
          <div className="flex flex-wrap gap-3">
            {['Chronic conditions', 'Medication management', 'Mental health', 'General health uncertainty'].map(d => (
              <span key={d} className="px-4 py-2 rounded-full border border-white/[0.08] text-xs sm:text-sm text-[#A1A1AA] font-medium">{d}</span>
            ))}
          </div>
        </R>
        <R delay={300}>
          <p className="text-sm text-[#71717A] mt-8">This is not a niche problem. It is a universal one.</p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 12 — EXPANSION
          ═══════════════════════════════════════════════════ */}
      <S className="py-24 sm:py-32">
        <R>
          <p className="text-xl sm:text-2xl font-light text-white leading-snug mb-4">
            Health is the starting point.
          </p>
        </R>
        <R delay={150}>
          <p className="text-base text-[#A1A1AA] mb-8">
            The same model applies wherever decisions are unclear:
          </p>
        </R>
        <R delay={250}>
          <div className="flex flex-wrap gap-3">
            {['Finance', 'Lifestyle', 'Performance'].map(d => (
              <span key={d} className="px-4 py-2 rounded-full border border-[#4FD1C5]/15 text-xs sm:text-sm text-[#4FD1C5]/80 font-medium">{d}</span>
            ))}
          </div>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 13 — CURRENT STATE
          ═══════════════════════════════════════════════════ */}
      <S className="py-20 sm:py-24">
        <R>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 mb-10">
            {['Live system', 'Real users', 'Subscription active', 'Core loop working', 'Early-stage validation in progress'].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA] font-medium tracking-wide">{s}</span>
              </div>
            ))}
          </div>
        </R>
        <R delay={150}>
          <p className="text-sm text-[#71717A] text-center">
            This is not a concept. This is already in motion.
          </p>
        </R>
      </S>

      <Divider />

      {/* ── Micro-confidence bridge ── */}
      <S className="py-12 sm:py-16">
        <R>
          <p className="text-sm text-[#71717A] text-center">
            This is the early stage of a much larger system.
          </p>
        </R>
      </S>

      <Divider />

      {/* ═══════════════════════════════════════════════════
          SECTION 14 — FINAL CLOSE
          ═══════════════════════════════════════════════════ */}
      <S className="py-32 sm:py-44 pb-36 sm:pb-52">
        <R>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-6">
            The world doesn't need<br />more information.
          </h2>
        </R>
        <R delay={150}>
          <p className="text-xl sm:text-2xl font-light text-white leading-snug mb-4">
            It needs clearer decisions.
          </p>
        </R>
        <R delay={300}>
          <p className="text-sm text-[#4FD1C5] font-medium mt-10">
            Kindra Health is built for that shift.
          </p>
        </R>
        <R delay={450}>
          <div className="mt-24">
            <img src="/kindra-brand-logo.png" alt="Kindra Health" className="h-8 w-8 object-contain opacity-30" />
          </div>
        </R>
      </S>
    </div>
  );
};

export default ExplainPage;
